import React, { useState, useEffect, useCallback} from 'react';
import {
    Box, Button, TextField, MenuItem, Select, InputLabel, FormControl,
    Typography, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Chip
} from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTheme } from '../../shared/context/ThemeContext';

const API_BASE = 'https://zakariabalali.nl/api';
const DATASETS = ['adjectives', 'adverbs', 'prepositions', 'pronouns', 'nouns', 'verbs'];
const DATASET_LABELS = {
    adjectives: 'Bijvoeglijke naamwoorden',
    adverbs: 'Bijwoorden',
    prepositions: 'Voorzetsels',
    pronouns: 'Voornaamwoorden',
    nouns: 'Zelfstandige naamwoorden',
    verbs: 'Werkwoorden',
};

function getTokens(isDark) {
    return {
        pageBg: isDark
            ? 'linear-gradient(135deg, #0d0d1a 0%, #0f0f23 35%, #0a1628 70%, #0d0d1a 100%)'
            : 'linear-gradient(135deg, #dddaf0 0%, #e0ddf2 35%, #d8e0f0 70%, #dddaf0 100%)',
        glow1: isDark ? 'rgba(108,99,255,0.13)' : 'rgba(108,99,255,0.08)',
        glow2: isDark ? 'rgba(37,117,252,0.10)' : 'rgba(37,117,252,0.06)',
        titleGradient: isDark
            ? 'linear-gradient(135deg, #fff 0%, #9d97ff 60%, #7878a0 100%)'
            : 'linear-gradient(135deg, #1a1830 0%, #4a40c0 60%, #6060a0 100%)',
        subtitle: isDark ? '#6060a0' : '#5050a0',
        text: isDark ? '#e8e8f0' : '#1a1830',
        textMuted: isDark ? '#7878a0' : '#5050a0',
        accent: isDark ? '#9d97ff' : '#4a40c0',
        panelBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.45)',
        panelBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,70,140,0.15)',
        panelShadow: isDark
            ? 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.3)'
            : 'inset 0 1px 0 rgba(255,255,255,0.8), 0 4px 24px rgba(80,70,140,0.12)',
        inputBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)',
        inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(80,70,140,0.2)',
        inputBorderHover: isDark ? 'rgba(108,99,255,0.5)' : 'rgba(85,72,224,0.5)',
        menuBg: isDark ? '#1a1a2e' : '#e8e6f8',
        menuBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,70,140,0.15)',
        menuItemText: isDark ? '#e8e8f0' : '#1a1830',
        menuItemHover: isDark ? 'rgba(108,99,255,0.15)' : 'rgba(85,72,224,0.12)',
        menuItemSelectedBg: isDark ? 'rgba(108,99,255,0.25)' : 'rgba(85,72,224,0.2)',
        menuItemSelectedText: isDark ? '#c8c4ff' : '#2a20b0',
        menuItemSelectedHover: isDark ? 'rgba(108,99,255,0.35)' : 'rgba(85,72,224,0.3)',
        divider: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(80,70,140,0.1)',
        rowHover: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(80,70,140,0.05)',
        rowDivider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,70,140,0.08)',
        accentBtnBg: isDark ? 'rgba(108,99,255,0.18)' : 'rgba(85,72,224,0.15)',
        accentBtnBorder: isDark ? 'rgba(108,99,255,0.35)' : 'rgba(85,72,224,0.35)',
        accentBtnText: isDark ? '#b0aaff' : '#3a2ec0',
        accentBtnHoverBg: isDark ? 'rgba(108,99,255,0.28)' : 'rgba(85,72,224,0.28)',
        accentBtnHoverBorder: isDark ? 'rgba(108,99,255,0.6)' : 'rgba(85,72,224,0.6)',
        accentBtnHoverText: isDark ? '#d0ccff' : '#2a1eb0',
        accentBtnStrongBg: isDark ? 'rgba(108,99,255,0.22)' : 'rgba(85,72,224,0.2)',
        accentBtnStrongText: isDark ? '#c8c4ff' : '#2a20b0',
        accentBtnStrongHoverBg: isDark ? 'rgba(108,99,255,0.35)' : 'rgba(85,72,224,0.32)',
        accentBtnStrongHoverText: isDark ? '#e0dcff' : '#1a10a0',
        plainBtnBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.5)',
        plainBtnBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(80,70,140,0.2)',
        plainBtnText: isDark ? '#a0a0c0' : '#4a4570',
        plainBtnHoverBg: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.7)',
        plainBtnHoverBorder: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(80,70,140,0.35)',
        plainBtnHoverText: isDark ? '#e8e8f0' : '#1a1830',
        iconBtnColor: isDark ? '#6060a0' : '#7070b0',
        iconBtnAccentHover: isDark ? '#9d97ff' : '#4a40c0',
        iconBtnAccentHoverBg: isDark ? 'rgba(108,99,255,0.15)' : 'rgba(85,72,224,0.12)',
        iconBtnAddBg: isDark ? 'rgba(108,99,255,0.15)' : 'rgba(85,72,224,0.12)',
        iconBtnAddBorder: isDark ? 'rgba(108,99,255,0.3)' : 'rgba(85,72,224,0.3)',
        iconBtnAddColor: isDark ? '#9d97ff' : '#4a40c0',
        iconBtnAddHoverBg: isDark ? 'rgba(108,99,255,0.28)' : 'rgba(85,72,224,0.25)',
        iconBtnDelHoverBg: isDark ? 'rgba(231,76,60,0.12)' : 'rgba(200,50,40,0.1)',
        dialogBg: isDark ? '#13131f' : '#e4e1f5',
        dialogBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(80,70,140,0.2)',
        dialogTitle: isDark ? '#e8e8f0' : '#1a1830',
        chipBg: isDark ? 'rgba(108,99,255,0.15)' : 'rgba(85,72,224,0.12)',
        chipText: isDark ? '#9d97ff' : '#4a40c0',
        chipBorder: isDark ? 'rgba(108,99,255,0.25)' : 'rgba(85,72,224,0.25)',
        cancelText: isDark ? '#7878a0' : '#5050a0',
        cancelHoverText: isDark ? '#e8e8f0' : '#1a1830',
        saveBtnBg: isDark ? 'rgba(108,99,255,0.3)' : 'rgba(85,72,224,0.25)',
        saveBtnBorder: isDark ? 'rgba(108,99,255,0.5)' : 'rgba(85,72,224,0.5)',
        saveBtnText: isDark ? '#d0ccff' : '#2a1eb0',
        saveBtnHoverBg: isDark ? 'rgba(108,99,255,0.5)' : 'rgba(85,72,224,0.42)',
    };
}

export default function UpdatePage() {
    const [dataset, setDataset] = useState('adjectives');
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [form, setForm] = useState({});
    const [editIndex, setEditIndex] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [allData, setAllData] = useState({});
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const t = getTokens(isDark);

    const textFieldSx = {
        '& .MuiInputBase-root': { borderRadius: 10, backgroundColor: t.inputBg, color: t.text },
        '& .MuiInputBase-input': { color: t.text },
        '& .MuiInputLabel-root': { color: t.textMuted },
        '& label.Mui-focused': { color: isDark ? '#6c63ff' : '#4a40c0' },
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: t.inputBorder },
            '&:hover fieldset': { borderColor: t.inputBorderHover },
            '&.Mui-focused fieldset': { borderColor: isDark ? '#6c63ff' : '#4a40c0', borderWidth: 2 },
        },
    };

    const selectSx = {
        backgroundColor: t.inputBg,
        color: t.text,
        borderRadius: 10,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: t.inputBorder },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.inputBorderHover },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#6c63ff' : '#4a40c0', borderWidth: '2px' },
        '& .MuiSvgIcon-root': { color: t.textMuted },
        '& .MuiSelect-select': { color: t.text },
    };

    const menuItemSx = {
        background: t.menuBg,
        color: t.menuItemText,
        fontSize: '0.92rem',
        '&:hover': { background: t.menuItemHover },
        '&.Mui-selected': {
            background: t.menuItemSelectedBg,
            color: t.menuItemSelectedText,
            fontWeight: 700,
            '&:hover': { background: t.menuItemSelectedHover },
        },
    };

    const panel = {
        background: t.panelBg,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: `1px solid ${t.panelBorder}`,
        borderRadius: '16px',
        boxShadow: t.panelShadow,
    };

    async function request(url, method = 'GET', body = null) {
        const options = { method, headers: { 'Content-Type': 'application/json' } };
        if (body) options.body = JSON.stringify(body);
        const res = await fetch(url, options);
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'Request failed');
        return result;
    }

    function isFormEmpty(f) {
        if (!f) return true;
        if (dataset === 'verbs') return !f.dutch || !f.darijaBase;
        return !f.dutch || !f.darija;
    }

    const loadData = useCallback(async () => {
        try {
            if (dataset === 'nouns') {
                const nouns = await request(`${API_BASE}/nouns`);
                setCategories(Object.keys(nouns));
                const raw = selectedCategory ? nouns[selectedCategory] || [] : [];
                setData(raw.slice().sort((a, b) => a.dutch.localeCompare(b.dutch, 'nl')));
                setAllData(nouns);
            } else if (dataset === 'verbs') {
                const verbs = await request(`${API_BASE}/verbs`);
                setData(verbs.slice().sort((a, b) => a.dutch.localeCompare(b.dutch, 'nl')));
            } else {
                const simple = await request(`${API_BASE}/${dataset}`);
                setData(simple.slice().sort((a, b) => a.dutch.localeCompare(b.dutch, 'nl')));
            }
        } catch (e) {
            console.error(e);
            setData([]);
        }
    }, [dataset, selectedCategory]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    const openAddDialog = () => { setForm(dataset === 'verbs' ? { dutch: '', darijaBase: '' } : {}); setIsEditing(false); setDialogOpen(true); };
    const openEditDialog = (item, idx) => { setForm(item); setEditIndex(idx); setIsEditing(true); setDialogOpen(true); };
    const handleCloseDialog = () => { setDialogOpen(false); setForm({}); setEditIndex(null); };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.setTextColor(176, 46, 12);
        doc.text(DATASET_LABELS[dataset] + ' Overzicht', 14, 20);
        let startY = 30;

        if (dataset === 'verbs') {
            autoTable(doc, { startY, head: [['Nederlands', 'Darija']], body: data.map((d) => [d.dutch, d.darijaBase]), theme: 'grid', headStyles: { fillColor: [108, 99, 255], textColor: 255 }, styles: { fontSize: 11 } });
        } else if (dataset === 'nouns') {
            (selectedCategory ? [selectedCategory] : categories).forEach((cat) => {
                doc.setFontSize(14);
                doc.setTextColor(0, 0, 0);
                doc.text(`Categorie: ${cat}`, 14, startY);
                startY += 6;
                autoTable(doc, { startY, head: [['Nederlands', 'Darija']], body: (selectedCategory ? data : (allData[cat] || [])).map((d) => [d.dutch, d.darija]), theme: 'grid', headStyles: { fillColor: [108, 99, 255], textColor: 255 }, styles: { fontSize: 11 } });
                startY = doc.lastAutoTable.finalY + 10;
                if (startY > 260) { doc.addPage(); startY = 20; }
            });
        } else {
            autoTable(doc, { startY, head: [['Nederlands', 'Darija']], body: data.map((d) => [d.dutch, d.darija]), theme: 'grid', headStyles: { fillColor: [108, 99, 255], textColor: 255 }, styles: { fontSize: 11 } });
        }
        doc.save(`${DATASET_LABELS[dataset]}.pdf`);
    };

    const handleSave = async () => {
        if (isFormEmpty(form)) return alert('Alle velden moeten ingevuld zijn!');
        try {
            if (isEditing) {
                if (dataset === 'nouns') await request(`${API_BASE}/nouns/${selectedCategory}/${editIndex}`, 'PUT', form);
                else if (dataset === 'verbs') await request(`${API_BASE}/verbs/${encodeURIComponent(form.dutch)}`, 'PUT', form);
                else await request(`${API_BASE}/${dataset}/${editIndex}`, 'PUT', form);
            } else {
                if (data.some((d) => d.dutch === form.dutch)) return alert('Dit Nederlandse woord bestaat al!');
                if (dataset === 'nouns') {
                    if (!selectedCategory) return alert('Selecteer eerst een categorie');
                    await request(`${API_BASE}/nouns/${selectedCategory}`, 'POST', form);
                } else if (dataset === 'verbs') {
                    await request(`${API_BASE}/verbs`, 'POST', form);
                } else {
                    await request(`${API_BASE}/${dataset}`, 'POST', form);
                }
            }
            handleCloseDialog();
            loadData();
        } catch (e) {
            console.error(e);
        }
    };

    const handleDelete = async (item, index = null) => {
        const label = (typeof item === 'object' ? item?.dutch : item) || 'dit item';
        if (!window.confirm(`"${label}" verwijderen?`)) return;
        try {
            if (dataset === 'nouns') {
                const allCatData = allData[selectedCategory] || [];
                const realIndex = allCatData.findIndex((d) => d.dutch === item.dutch && d.darija === item.darija);
                await request(`${API_BASE}/nouns/${selectedCategory}/${realIndex}`, 'DELETE');
            } else if (dataset === 'verbs') {
                const key = typeof item === 'object' ? item.dutch : item;
                await request(`${API_BASE}/verbs/${encodeURIComponent(key)}`, 'DELETE');
            } else {
                const simple = await request(`${API_BASE}/${dataset}`);
                const realIndex = simple.findIndex((d) => d.dutch === item.dutch && d.darija === item.darija);
                await request(`${API_BASE}/${dataset}/${realIndex}`, 'DELETE');
            }
            handleCloseDialog();
            loadData();
        } catch (e) {
            console.error(e);
        }
    };

    const handleAddCategory = async () => {
        const name = prompt('Voer de nieuwe categorienaam in:');
        if (!name) return;
        try {
            await request(`${API_BASE}/nouns/${name}`, 'POST', {});
            setSelectedCategory(name);
            loadData();
        } catch (e) {
            console.error(e);
        }
    };

    const handleDeleteCategory = async () => {
        if (!selectedCategory) return;
        if (!window.confirm(`Categorie "${selectedCategory}" verwijderen?`)) return;
        try {
            await request(`${API_BASE}/nouns/${selectedCategory}`, 'DELETE');
            setSelectedCategory('');
            loadData();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: t.pageBg,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'fixed', top: '-15%', right: '-5%',
                width: '55vw', height: '55vw', borderRadius: '50%',
                background: `radial-gradient(circle, ${t.glow1} 0%, transparent 65%)`,
                pointerEvents: 'none', zIndex: 0,
            },
            '&::after': {
                content: '""',
                position: 'fixed', bottom: '-15%', left: '-5%',
                width: '45vw', height: '45vw', borderRadius: '50%',
                background: `radial-gradient(circle, ${t.glow2} 0%, transparent 65%)`,
                pointerEvents: 'none', zIndex: 0,
            },
        }}>
            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 6 }, position: 'relative', zIndex: 1 }}>

                {/* ── Header ── */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Box>
                        <Typography sx={{
                            fontFamily: "'Syne', sans-serif", fontWeight: 800,
                            fontSize: { xs: '1.8rem', sm: '2.4rem' }, letterSpacing: '-0.03em',
                            background: t.titleGradient,
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                        }}>
                            Woordenboek
                        </Typography>
                        <Typography sx={{ color: t.subtitle, fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', mt: 0.5 }}>
                            {data.length} {data.length === 1 ? 'woord' : 'woorden'} · {DATASET_LABELS[dataset]}
                        </Typography>
                    </Box>
                    <Button onClick={() => navigate('/darija')} sx={{
                        background: t.accentBtnBg, backdropFilter: 'blur(8px)',
                        border: `1px solid ${t.accentBtnBorder}`, color: t.accentBtnText,
                        fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                        textTransform: 'none', borderRadius: '10px', px: 2.5, py: 1,
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
                        '&:hover': { background: t.accentBtnHoverBg, borderColor: t.accentBtnHoverBorder, color: t.accentBtnHoverText },
                    }}>
                        ← Terug
                    </Button>
                </Box>

                {/* ── Controls panel ── */}
                <Box sx={{ ...panel, p: { xs: 2.5, sm: 3 }, mb: 3 }}>
                    <Typography sx={{ color: t.subtitle, fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 2 }}>
                        Instellingen
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <FormControl sx={{ minWidth: 220, '& .MuiInputLabel-root': { color: t.textMuted }, '& .MuiInputLabel-root.Mui-focused': { color: isDark ? '#6c63ff' : '#4a40c0' } }}>
                            <InputLabel>Woordsoort</InputLabel>
                            <Select sx={selectSx}
                                value={dataset}
                                label="Woordsoort"
                                onChange={(e) => { setDataset(e.target.value); setSelectedCategory(''); setForm({}); }}
                                MenuProps={{ PaperProps: { sx: { background: t.menuBg, border: `1px solid ${t.menuBorder}`, borderRadius: '10px', mt: 0.5 } } }}
                            >
                                {DATASETS.map((d) => <MenuItem key={d} value={d} sx={menuItemSx}>{DATASET_LABELS[d]}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {dataset === 'nouns' && (
                            <FormControl sx={{ minWidth: 220, '& .MuiInputLabel-root': { color: t.textMuted }, '& .MuiInputLabel-root.Mui-focused': { color: isDark ? '#6c63ff' : '#4a40c0' } }}>
                                <InputLabel>Categorie</InputLabel>
                                <Select sx={selectSx}
                                    value={selectedCategory}
                                    label="Categorie"
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    MenuProps={{ PaperProps: { sx: { background: t.menuBg, border: `1px solid ${t.menuBorder}`, borderRadius: '10px', mt: 0.5 } } }}
                                >
                                    <MenuItem value="" sx={{ ...menuItemSx, color: t.subtitle, fontStyle: 'italic' }}>— alle categorieën —</MenuItem>
                                    {categories.map((c) => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}

                        {dataset === 'nouns' && (
                            <Box display="flex" gap={1} alignItems="center">
                                <IconButton onClick={handleAddCategory} size="small" sx={{
                                    background: t.iconBtnAddBg, border: `1px solid ${t.iconBtnAddBorder}`,
                                    color: t.iconBtnAddColor, borderRadius: '8px',
                                    '&:hover': { background: t.iconBtnAddHoverBg, borderColor: isDark ? '#6c63ff' : '#4a40c0' },
                                }}>
                                    <Add fontSize="small" />
                                </IconButton>
                                <IconButton onClick={handleDeleteCategory} size="small" disabled={!selectedCategory} sx={{
                                    background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.25)',
                                    color: '#e74c3c', borderRadius: '8px',
                                    '&:hover': { background: 'rgba(231,76,60,0.2)', borderColor: '#e74c3c' },
                                    '&.Mui-disabled': { opacity: 0.3 },
                                }}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ── Actions bar ── */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                    <Typography sx={{ color: t.textMuted, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem' }}>
                        {data.length > 0 ? `${data.length} items` : 'Geen items'}
                    </Typography>
                    <Box display="flex" gap={1.5}>
                        <Button onClick={exportToPDF} sx={{
                            background: t.plainBtnBg, border: `1px solid ${t.plainBtnBorder}`,
                            color: t.plainBtnText, fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                            textTransform: 'none', borderRadius: '10px', px: 2, fontSize: '0.85rem',
                            '&:hover': { background: t.plainBtnHoverBg, borderColor: t.plainBtnHoverBorder, color: t.plainBtnHoverText },
                        }}>
                            PDF exporteren
                        </Button>
                        <Button onClick={openAddDialog} sx={{
                            background: t.accentBtnStrongBg, backdropFilter: 'blur(8px)',
                            border: `1px solid ${t.accentBtnBorder}`, color: t.accentBtnStrongText,
                            fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
                            textTransform: 'none', borderRadius: '10px', px: 2.5, fontSize: '0.85rem',
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)',
                            '&:hover': { background: t.accentBtnStrongHoverBg, borderColor: t.accentBtnHoverBorder, color: t.accentBtnStrongHoverText },
                        }}>
                            + Nieuw woord
                        </Button>
                    </Box>
                </Box>

                {/* ── Word list ── */}
                <Box sx={{ ...panel, overflow: 'hidden' }}>
                    {data.length === 0 ? (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Typography sx={{ color: t.subtitle, fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem' }}>
                                {dataset === 'nouns' && !selectedCategory ? 'Selecteer een categorie om woorden te zien' : 'Geen woorden gevonden'}
                            </Typography>
                        </Box>
                    ) : (
                        data.map((item, idx) => (
                            <Box key={dataset === 'verbs' ? item.dutch : idx} sx={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                px: { xs: 2, sm: 3 }, py: 1.8,
                                borderBottom: idx < data.length - 1 ? `1px solid ${t.divider}` : 'none',
                                transition: 'background 0.15s',
                                '&:hover': { background: t.rowHover },
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 4 }, flex: 1, minWidth: 0 }}>
                                    <Typography sx={{
                                        color: t.text, fontFamily: "'DM Sans', sans-serif",
                                        fontWeight: 600, fontSize: '0.95rem',
                                        minWidth: { xs: 100, sm: 160 }, flexShrink: 0,
                                    }}>
                                        {item.dutch}
                                    </Typography>
                                    <Box sx={{ width: '1px', height: 18, background: t.rowDivider, flexShrink: 0 }} />
                                    <Typography sx={{
                                        color: t.accent, fontFamily: "'DM Sans', sans-serif",
                                        fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {dataset === 'verbs' ? item.darijaBase : item.darija}
                                    </Typography>
                                    {dataset === 'verbs' && (
                                        <Chip label="werkwoord" size="small" sx={{
                                            background: t.chipBg, color: t.chipText,
                                            border: `1px solid ${t.chipBorder}`, fontSize: '0.7rem',
                                            height: 20, flexShrink: 0,
                                        }} />
                                    )}
                                </Box>
                                <Box display="flex" gap={0.5} ml={2} flexShrink={0}>
                                    <IconButton onClick={() => openEditDialog(item, idx)} size="small" sx={{
                                        color: t.iconBtnColor, borderRadius: '8px',
                                        '&:hover': { color: t.iconBtnAccentHover, background: t.iconBtnAccentHoverBg },
                                    }}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(item, idx)} size="small" sx={{
                                        color: t.iconBtnColor, borderRadius: '8px',
                                        '&:hover': { color: '#e74c3c', background: t.iconBtnDelHoverBg },
                                    }}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            </Box>

            {/* ── Dialog ── */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md"
                PaperProps={{ sx: { background: t.dialogBg, border: `1px solid ${t.dialogBorder}`, borderRadius: '18px', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' } }}
            >
                <DialogTitle sx={{ color: t.dialogTitle, fontFamily: "'Syne', sans-serif", fontWeight: 700, pb: 1 }}>
                    {isEditing ? 'Woord bewerken' : 'Nieuw woord toevoegen'}
                </DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    {dataset === 'verbs' ? (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField sx={textFieldSx} fullWidth label="Nederlands" value={form.dutch || ''} onChange={(e) => setForm({ ...form, dutch: e.target.value })} />
                            <TextField sx={textFieldSx} fullWidth label="Darija" value={form.darijaBase || ''} onChange={(e) => setForm({ ...form, darijaBase: e.target.value })} />
                        </Box>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField sx={textFieldSx} fullWidth name="dutch" label="Nederlands" value={form.dutch || ''} onChange={handleFormChange} />
                            <TextField sx={textFieldSx} fullWidth name="darija" label="Darija" value={form.darija || ''} onChange={handleFormChange} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button onClick={handleCloseDialog} sx={{ color: t.cancelText, textTransform: 'none', fontFamily: "'DM Sans', sans-serif", '&:hover': { color: t.cancelHoverText } }}>
                        Annuleer
                    </Button>
                    {isEditing && (
                        <Button onClick={() => handleDelete(form, editIndex)} sx={{
                            color: '#e74c3c', textTransform: 'none', fontFamily: "'DM Sans', sans-serif",
                            background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: '8px', px: 2,
                            '&:hover': { background: 'rgba(231,76,60,0.2)' },
                        }}>
                            Verwijderen
                        </Button>
                    )}
                    <Button variant="contained" onClick={handleSave} sx={{
                        background: t.saveBtnBg, border: `1px solid ${t.saveBtnBorder}`,
                        color: t.saveBtnText, textTransform: 'none', fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 700, borderRadius: '8px', px: 3, boxShadow: 'none',
                        '&:hover': { background: t.saveBtnHoverBg, boxShadow: 'none' },
                    }}>
                        {isEditing ? 'Bijwerken' : 'Toevoegen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
